import * as Blockly from 'blockly/core';
import 'blockly/javascript';
import { javascriptGenerator } from 'blockly/javascript';

// Type definitions
interface RuntimeContext {
  ui: UIRuntime;
  ai: AIRuntime;
  variables: Map<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventListeners: Map<string, ((...args: any[]) => void)[]>;
  isRunning: boolean;
  outputElement: HTMLElement | null;
}

export interface UIRuntimeOptions {
  visualOutputContainers?: (HTMLElement | null)[];
  consoleOutputContainer?: HTMLElement | null;
  puzzleGrid?: {
    rows: number;
    cols: number;ok
    start: { x: number; y: number; dir: 'N' | 'E' | 'S' | 'W' };
    end: { x: number; y: number; };
    obstacles: { x: number; y: number; }[];
  };
  onPuzzleComplete?: () => void;
}

interface ExecutionResult {
  success: boolean;
  output: (string | { type: 'button'; id: string; text: string })[];
  logs: { message: string; type: 'info' | 'success' | 'warning' | 'error' }[];
  error?: string;
  backgroundColor?: string;
}

// UI Runtime for handling UI operations
export class UIRuntime {
  private consoleContainer: HTMLElement | null;
  private visualOutputContainers: (HTMLElement | null)[];
  public logs: ExecutionResult['logs'] = [];
  public output: ExecutionResult['output'] = [];
  public backgroundColor = '';

  constructor(options: { 
    consoleOutputContainer?: HTMLElement | null;
    visualOutputContainers?: (HTMLElement | null)[];
  }) {
    this.consoleContainer = options.consoleOutputContainer || null;
    this.visualOutputContainers = options.visualOutputContainers || [];
  }

  log(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    this.logs.push({ message, type });

    if (!this.consoleContainer) return;

    const entry = document.createElement('div');
    entry.className = 'console-entry';
    
    const timestamp = new Date().toLocaleTimeString();
    let color = 'text-gray-300';
    let icon = 'ℹ️';

    switch(type) {
      case 'success':
        color = 'text-green-400';
        icon = '✅';
        break;
      case 'warning':
        color = 'text-yellow-400';
        icon = '⚠️';
        break;
      case 'error':
        color = 'text-red-400';
        icon = '❌';
        break;
    }

    entry.innerHTML = `<span class="text-gray-500 mr-2">[${timestamp}]</span> <span class="mr-2">${icon}</span> <span class="${color}">${message}</span>`;
    this.consoleContainer.appendChild(entry);
    this.consoleContainer.scrollTop = this.consoleContainer.scrollHeight;
  }

  showText(text: string): void {
    this.output.push(text);
    // Actually render to visual output containers
    this.visualOutputContainers.forEach(container => {
      if (container) {
        const textElement = document.createElement('p');
        textElement.textContent = text;
        textElement.className = 'mb-2 text-gray-800';
        container.appendChild(textElement);
      }
    });
  }

  createButton(id: string, text: string): void {
    this.output.push({ type: 'button', id, text });
    // Actually render to visual output containers
    this.visualOutputContainers.forEach(container => {
      if (container) {
        const button = document.createElement('button');
        button.id = id;
        button.textContent = text;
        button.className = 'bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded mr-2 mb-2';
        container.appendChild(button);
      }
    });
  }

  setBackgroundColor(color: string): void {
    this.backgroundColor = color;
    // Apply background color to visual output containers
    this.visualOutputContainers.forEach(container => {
      if (container) {
        container.style.backgroundColor = color;
      }
    });
  }

  playSound(soundType: string): void {
    try {
      // Create audio context for sound generation
      const audioContext = new (window.AudioContext || (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)();
      if (!audioContext) {
          console.warn('Audio playback not supported: Could not create AudioContext.');
          return;
      }
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Different sound types
      switch (soundType) {
        case 'beep':
          oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
          break;
        case 'click':
          oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
          break;
        case 'success':
          oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
          break;
        case 'error':
          oscillator.frequency.setValueAtTime(300, audioContext.currentTime);
          break;
        default:
          oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
      }

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('Audio playback not supported:', error);
    }
  }

  playSpeech(text: string): void {
    if (!('speechSynthesis' in window)) {
      this.log('Speech synthesis not supported in this browser.', 'error');
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    window.speechSynthesis.speak(utterance);
  }

  clearOutput(): void {
    this.output = [];
    this.backgroundColor = '';
    // Clear visual output containers
    this.visualOutputContainers.forEach(container => {
      if (container) {
        container.innerHTML = '';
        container.style.backgroundColor = '';
      }
    });
    // We don't clear logs here, run() method is responsible for that.
  }
}

// AI Runtime for handling AI operations (placeholder for now)
class AIRuntime {
  async classifyImage(imageData: string, modelId: string): Promise<string> {
    // Placeholder implementation
    console.log(`Classifying image with model: ${modelId}`);
    
    // Simulate AI processing delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Return mock classification result
    const mockResults = ['cat', 'dog', 'bird', 'car', 'flower'];
    return mockResults[Math.floor(Math.random() * mockResults.length)];
  }

  async analyzeSentiment(text: string): Promise<string> {
    // Simple sentiment analysis simulation
    const positiveWords = ['good', 'great', 'awesome', 'excellent', 'wonderful', 'amazing'];
    const negativeWords = ['bad', 'terrible', 'awful', 'horrible', 'worst', 'hate'];
    
    const lowerText = text.toLowerCase();
    const hasPositive = positiveWords.some(word => lowerText.includes(word));
    const hasNegative = negativeWords.some(word => lowerText.includes(word));
    
    if (hasPositive && !hasNegative) return 'positive';
    if (hasNegative && !hasPositive) return 'negative';
    return 'neutral';
  }
}

// Main Runtime Engine
export class BlocklyRuntime {
  public ui: UIRuntime;
  private context: RuntimeContext;
  private workspace: Blockly.WorkspaceSvg | null = null;
  private puzzleGrid: UIRuntimeOptions['puzzleGrid'];
  private onPuzzleComplete: UIRuntimeOptions['onPuzzleComplete'];
  private heroState: { x: number; y: number; dir: 'N' | 'E' | 'S' | 'W' };
  private eventHandlers: Map<string, { [key: string]: ((...args: unknown[]) => void)[] }> = new Map();

  constructor(options: UIRuntimeOptions) {
    this.ui = new UIRuntime({
      consoleOutputContainer: options.consoleOutputContainer || null,
      visualOutputContainers: options.visualOutputContainers || [],
    });
    
    this.puzzleGrid = options.puzzleGrid;
    this.onPuzzleComplete = options.onPuzzleComplete;
    this.heroState = this.puzzleGrid ? { ...this.puzzleGrid.start } : { x: 0, y: 0, dir: 'N' };

    this.context = {
      ui: this.ui,
      ai: new AIRuntime(),
      variables: new Map(),
      eventListeners: new Map(),
      isRunning: false,
      outputElement: null, // Deprecated: no longer directly referencing DOM
    };
  }

  setWorkspace(workspace: Blockly.WorkspaceSvg): void {
    this.workspace = workspace;
    // Initialize the JavaScript generator with the workspace's variable map
    // This uses the modern Blockly v12+ API and prevents our code from using deprecated methods
    javascriptGenerator.init(workspace);
    this.ui.setBackgroundColor(''); 
  }

  log(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    this.ui.log(message, type);
  }

  clearOutput(): void {
    this.ui.clearOutput();
    this.ui.setBackgroundColor(''); 
  }

  on(elementId: string, eventType: string, callback: (...args: unknown[]) => void): void {
    if (!this.eventHandlers.has(elementId)) {
      this.eventHandlers.set(elementId, {});
    }
    const elementEvents = this.eventHandlers.get(elementId)!;
    if (!elementEvents[eventType]) {
      elementEvents[eventType] = [];
    }
    elementEvents[eventType].push(callback);
    this.log(`Registered event handler for ${eventType} on element #${elementId}`, 'info');
    
    // Try to attach listener to already existing element
    const element = this.context.outputElement?.querySelector(`#${elementId}`);
    if (element) {
        this.attachEventListeners(elementId, element as HTMLElement);
    }
  }

  private attachEventListeners(elementId: string, element: HTMLElement): void {
    const handlers = this.eventHandlers.get(elementId);
    if (handlers) {
      for (const eventType in handlers) {
        if (Object.prototype.hasOwnProperty.call(handlers, eventType)) {
            handlers[eventType].forEach(callback => {
              element.addEventListener(eventType, () => callback());
            });
        }
      }
    }
  }

  // --- Game-specific methods for PuzzleMaster ---
  moveForward(): void {
    if (!this.puzzleGrid) return;
    
    let { x, y } = this.heroState;
    const { dir } = this.heroState;

    if (dir === 'N') y -= 1;
    else if (dir === 'E') x += 1;
    else if (dir === 'S') y += 1;
    else if (dir === 'W') x -= 1;

    // Check boundaries and obstacles
    if (
      x >= 0 && x < this.puzzleGrid.cols &&
      y >= 0 && y < this.puzzleGrid.rows &&
      !this.puzzleGrid.obstacles.some(obs => obs.x === x && obs.y === y)
    ) {
      this.heroState.x = x;
      this.heroState.y = y;
      this.log(`Moved to (${x}, ${y})`, 'info');
      this.checkCompletion();
    } else {
      this.log('Move failed: Hit an obstacle or boundary!', 'error');
      this.stop();
    }
  }

  turnLeft(): void {
    const directions: ('N' | 'E' | 'S' | 'W')[] = ['N', 'W', 'S', 'E'];
    const currentDirIndex = directions.indexOf(this.heroState.dir);
    this.heroState.dir = directions[(currentDirIndex + 1) % 4];
    this.log(`Turned left. New direction: ${this.heroState.dir}`, 'info');
  }

  turnRight(): void {
    const directions: ('N' | 'E' | 'S' | 'W')[] = ['N', 'E', 'S', 'W'];
    const currentDirIndex = directions.indexOf(this.heroState.dir);
    this.heroState.dir = directions[(currentDirIndex + 1) % 4];
    this.log(`Turned right. New direction: ${this.heroState.dir}`, 'info');
  }

  private checkCompletion(): void {
    if (
      this.puzzleGrid &&
      this.heroState.x === this.puzzleGrid.end.x &&
      this.heroState.y === this.puzzleGrid.end.y
    ) {
      this.log('Puzzle Complete!', 'success');
      if (this.onPuzzleComplete) {
        this.onPuzzleComplete();
      }
      this.stop();
    }
  }

  async run(code: string): Promise<ExecutionResult> {
    if (this.context.isRunning) {
        this.log('A program is already running.', 'warning');
        return { success: false, error: 'Already running', logs: this.ui.logs, output: [] };
    }
    
    this.clearOutput();
    this.ui.logs = []; // Clear logs for new run
    this.eventHandlers.clear();
    this.context.isRunning = true;
    this.context.variables.clear();
    
    if (this.puzzleGrid) {
        this.heroState = { ...this.puzzleGrid.start };
    }

    this.log('Executing program...', 'info');

    try {
        // The robust way to create a new async function from a string of code.
        const AsyncFunction = Object.getPrototypeOf(async function(){}).constructor;
        
        // Create a function that takes our context objects as arguments.
        const func = new AsyncFunction('ui', 'ai', 'variables', code.trim());
        
        // Execute the function, passing the context objects as arguments.
        await func(this.context.ui, this.context.ai, this.context.variables);
        
        this.log('Program finished.', 'success');
        return { 
          success: true, 
          logs: this.ui.logs, 
          output: this.ui.output,
          backgroundColor: this.ui.backgroundColor 
        };
    } catch (e) {
        const error = e as Error;
        this.log(`Execution Error: ${error.message}`, 'error');
        console.error('Blockly runtime error:', error); // Log the full error for better debugging.
        return { 
          success: false, 
          error: error.message, 
          logs: this.ui.logs, 
          output: this.ui.output, 
          backgroundColor: this.ui.backgroundColor
        };
    } finally {
        this.context.isRunning = false;
    }
  }

  stop(): void {
    this.context.isRunning = false;
    this.log('Program stopped.', 'warning');
  }

  isRunning(): boolean {
    return this.context.isRunning;
  }

  getGlobalVariables(): { [key: string]: unknown } {
    const variables: { [key: string]: unknown } = {};
    if (this.workspace) {
      this.workspace.getVariableMap().getAllVariables().forEach(variable => {
        variables[variable.getName()] = null; // Can't get value directly from workspace
      });
    }
    return variables;
  }
}